import { QueryClient, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { OFFLINE_MSG } from "@/lib/offline";
import { clearLegacyApiCaches, clearQueryPersist } from "@/lib/queryPersist";

function assertOnlineForMutation(): void {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error(OFFLINE_MSG);
  }
}

/**
 * Batch B: offlineFirst + reconnect so hydrated/persisted data remains visible
 * offline, and online return refetches active queries (last-synced via dataUpdatedAt).
 * Auth-scoped Supabase GETs are still NOT Workbox-cached (see src/sw.ts).
 */
export const appQueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onMutate: async () => {
      assertOnlineForMutation();
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : String(error);
      if (msg === OFFLINE_MSG || /failed to fetch|networkerror|offline|load failed/i.test(msg)) {
        toast.error(OFFLINE_MSG);
      }
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60 * 24,
      networkMode: "offlineFirst",
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (typeof navigator !== "undefined" && !navigator.onLine) return false;
        const msg = error instanceof Error ? error.message : String(error);
        if (/failed to fetch|networkerror|offline|load failed/i.test(msg)) return false;
        return failureCount < 2;
      }
    },
    mutations: {
      networkMode: "online"
    }
  }
});

export function createAppQueryClient(): QueryClient {
  return appQueryClient;
}

export async function wipeClientCaches(userId?: string | null): Promise<void> {
  appQueryClient.clear();
  await clearQueryPersist(userId);
  await clearLegacyApiCaches();
  // PersistQueryClientProvider unmount/remount can dehydrate once more after clear (#12).
  await new Promise((r) => setTimeout(r, 500));
  await clearQueryPersist(userId);
}
